import React from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { mediaUrl } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Leaf, Droplets, BadgeCheck } from "lucide-react";

export const LotCard = ({ lot }) => {
  const { t, formatPrice, formatNumber } = useApp();
  const img = lot.photos && lot.photos.length ? mediaUrl(lot.photos[0]) : lot.image;

  return (
    <Link
      to={`/marche/lot/${lot.id}`}
      data-testid={`lot-card-${lot.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-border bg-white transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/5"
    >
      <div className="relative h-44 overflow-hidden bg-secondary">
        {img && (
          <img
            src={img}
            alt={lot.title}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        )}
        <div className="absolute inset-x-0 top-0 flex items-start justify-between p-3">
          <Badge className="bg-primary text-primary-foreground hover:bg-primary">
            {t(`type.${lot.type}`)}
          </Badge>
          <div className="flex flex-col items-end gap-1.5">
            {lot.verified && (
              <Badge className="gap-1 bg-white text-primary hover:bg-white" data-testid="lot-verified-badge">
                <BadgeCheck className="h-3 w-3" /> {t("badge.verified")}
              </Badge>
            )}
            {lot.organic && (
              <Badge className="gap-1 bg-accent text-accent-foreground hover:bg-accent">
                <Leaf className="h-3 w-3" /> {t("lots.organic")}
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-center gap-1 text-xs text-neutral-500">
          <MapPin className="h-3.5 w-3.5" /> {lot.region} · {lot.seller_name}
        </div>
        <h3 className="mt-1.5 line-clamp-2 font-serif text-lg font-semibold leading-snug text-neutral-900">
          {lot.title}
        </h3>
        {lot.reference && <p className="mt-0.5 font-mono text-[10px] uppercase tracking-wider text-neutral-400" data-testid="lotcard-ref">{lot.reference}</p>}

        <div className="mt-2 flex flex-wrap gap-2 text-xs text-neutral-600">
          <span className="rounded bg-secondary px-2 py-1">{t(`quality.${lot.quality}`)}</span>
          {lot.type === "conditionne" ? (
            <>
              {lot.packaging_type && <span className="rounded bg-secondary px-2 py-1">{t(`packaging.${lot.packaging_type}`)}</span>}
              {lot.volume_ml != null && <span className="rounded bg-secondary px-2 py-1">{lot.volume_ml >= 1000 ? `${lot.volume_ml / 1000} L` : `${lot.volume_ml} ml`}</span>}
              {lot.white_label && <span className="rounded bg-secondary px-2 py-1">{t("cond.whiteLabelBadge")}</span>}
            </>
          ) : (
            <>
              {lot.variety && lot.variety !== "—" && <span className="rounded bg-secondary px-2 py-1">{lot.variety}</span>}
              {lot.acidity && (
                <span className="flex items-center gap-1 rounded bg-secondary px-2 py-1">
                  <Droplets className="h-3 w-3" /> {lot.acidity}
                </span>
              )}
            </>
          )}
        </div>

        <div className="mt-3 text-sm text-neutral-500">
          {lot.type === "conditionne" ? (
            <span><span className="font-medium text-neutral-800">{formatNumber(lot.quantity)}</span> {t("cond.units")}</span>
          ) : (
            <>{t("lots.quantity")}: <span className="font-medium text-neutral-800">{formatNumber(lot.quantity)} {lot.unit}</span></>
          )}
        </div>

        <div className="mt-auto flex items-end justify-between pt-4">
          <div>
            <p className="font-serif text-2xl font-bold text-primary">{formatPrice(lot.price_tnd)}</p>
            <p className="text-xs text-neutral-400">{lot.type === "conditionne" ? t("cond.perUnit") : t("lots.perUnit", { unit: lot.unit })}</p>
          </div>
          <Button variant="outline" size="sm" className="pointer-events-none border-primary/30 text-primary">
            {t("lots.view")}
          </Button>
        </div>
      </div>
    </Link>
  );
};
